import { PYTHON_BACKEND_URL } from "../constants/backend";
import { getResolvedPythonBackendUrl } from "./backendUrlResolver";
const CACHE_VERSION = 1;
const CACHE_KEY = "carousel_backend_settings_cache_v1";
const COLOR_FIELDS = [
    "excelHardcodedFontColor",
    "excelCalculationFontColor",
    "excelReferenceOtherSheetFontColor",
    "excelReferenceOtherWorkbookFontColor",
    "excelReferenceExternalDataFontColor",
];
export const DEFAULT_BACKEND_CUSTOM_INSTRUCTIONS = {
    carouselChat: [],
    search: [],
    research: [],
    execution: [],
};
export const DEFAULT_BACKEND_EXCEL_COLORS = {
    isExcelSemanticFormattingEnabled: true,
    excelApplyColorsOnlyToEmptyCells: true,
    excelHardcodedFontColor: "#0000FF",
    excelCalculationFontColor: "#000000",
    excelReferenceOtherSheetFontColor: "#1E914E",
    excelReferenceOtherWorkbookFontColor: "#8B0000",
    excelReferenceExternalDataFontColor: "#E30000",
};
function cloneCustomInstructions(value) {
    return {
        carouselChat: [...(value.carouselChat || [])],
        search: [...(value.search || [])],
        research: [...(value.research || [])],
        execution: [...(value.execution || [])],
    };
}
function cloneExcelColors(value) {
    return {
        ...value,
    };
}
function normalizeHexColor(input, fallback) {
    if (typeof input !== "string") {
        return fallback;
    }
    const raw = input.trim();
    const candidate = raw.startsWith("#") ? raw.slice(1) : raw;
    if (!/^[0-9A-Fa-f]{6}$/.test(candidate)) {
        return fallback;
    }
    return `#${candidate.toUpperCase()}`;
}
function normalizeCustomInstruction(item) {
    if (!item || typeof item !== "object" || Array.isArray(item)) {
        return null;
    }
    const value = item;
    const id = typeof value.id === "string" ? value.id.trim() : "";
    const title = typeof value.title === "string" ? value.title.trim() : "";
    const instruction = typeof value.instruction === "string" ? value.instruction.trim() : "";
    if (!id || !instruction) {
        return null;
    }
    return {
        id,
        title: title || "Instruction",
        instruction,
        on: Boolean(value.on),
    };
}
function normalizeCustomInstructions(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return cloneCustomInstructions(DEFAULT_BACKEND_CUSTOM_INSTRUCTIONS);
    }
    const source = value;
    const normalizeList = (field) => {
        const rawList = source[field];
        if (!Array.isArray(rawList)) {
            return [];
        }
        const items = [];
        for (const rawItem of rawList) {
            const normalized = normalizeCustomInstruction(rawItem);
            if (normalized) {
                items.push(normalized);
            }
        }
        return items;
    };
    return {
        carouselChat: normalizeList("carouselChat"),
        search: normalizeList("search"),
        research: normalizeList("research"),
        execution: normalizeList("execution"),
    };
}
function normalizeExcelColors(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return cloneExcelColors(DEFAULT_BACKEND_EXCEL_COLORS);
    }
    const source = value;
    const normalized = {
        ...DEFAULT_BACKEND_EXCEL_COLORS,
        isExcelSemanticFormattingEnabled: Boolean(source.isExcelSemanticFormattingEnabled),
        excelApplyColorsOnlyToEmptyCells: Boolean(source.excelApplyColorsOnlyToEmptyCells),
    };
    for (const colorField of COLOR_FIELDS) {
        normalized[colorField] = normalizeHexColor(source[colorField], DEFAULT_BACKEND_EXCEL_COLORS[colorField]);
    }
    return normalized;
}
function getBackendBaseUrl() {
    return getResolvedPythonBackendUrl(PYTHON_BACKEND_URL).replace(/\/+$/, "");
}
function readCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) {
            return { version: CACHE_VERSION };
        }
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") {
            return { version: CACHE_VERSION };
        }
        return { version: CACHE_VERSION, ...parsed };
    }
    catch {
        return { version: CACHE_VERSION };
    }
}
function writeCache(next) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
            ...next,
            version: CACHE_VERSION,
            updatedAt: new Date().toISOString(),
        }));
    }
    catch {
        // Local cache failure should never block settings updates.
    }
}
async function requestJson(args) {
    const url = new URL(`${getBackendBaseUrl()}${args.path}`);
    if (args.query) {
        for (const [key, value] of Object.entries(args.query)) {
            if (value) {
                url.searchParams.set(key, value);
            }
        }
    }
    const response = await fetch(url.toString(), {
        method: args.method,
        headers: {
            "Content-Type": "application/json",
        },
        ...(args.payload ? { body: JSON.stringify(args.payload) } : {}),
    });
    if (!response.ok) {
        throw new Error(`Settings request failed (${response.status}): ${await response.text()}`);
    }
    return (await response.json());
}
export class BackendSettingsService {
    constructor() {
        this.flushPromise = null;
    }
    getCurrentMode() {
        try {
            const office = globalThis.Office;
            const host = String(office?.context?.host || "").toLowerCase();
            if (host.includes("excel")) {
                return "excel";
            }
        }
        catch {
            // Fall through to default.
        }
        return "powerpoint";
    }
    getCachedCustomInstructions(mode) {
        const cache = readCache();
        const value = cache.customInstructions?.[mode];
        return value ? normalizeCustomInstructions(value) : null;
    }
    getCachedExcelColors() {
        const cache = readCache();
        return cache.excelColors ? normalizeExcelColors(cache.excelColors) : null;
    }
    async getCustomInstructions(mode) {
        const response = await requestJson({
            path: "/api/settings/custom-instructions",
            method: "GET",
            query: { mode },
        });
        const normalized = normalizeCustomInstructions(response.customInstructions);
        const cache = readCache();
        cache.customInstructions = { ...(cache.customInstructions || {}), [mode]: normalized };
        if (cache.pendingCustomInstructions?.[mode]) {
            delete cache.pendingCustomInstructions[mode];
        }
        writeCache(cache);
        return normalized;
    }
    async putCustomInstructions(mode, customInstructions) {
        const normalized = normalizeCustomInstructions(customInstructions);
        const cache = readCache();
        cache.customInstructions = { ...(cache.customInstructions || {}), [mode]: normalized };
        writeCache(cache);
        try {
            const response = await requestJson({
                path: "/api/settings/custom-instructions",
                method: "PUT",
                query: { mode },
                payload: normalized,
            });
            const saved = normalizeCustomInstructions(response.customInstructions || normalized);
            const nextCache = readCache();
            nextCache.customInstructions = { ...(nextCache.customInstructions || {}), [mode]: saved };
            if (nextCache.pendingCustomInstructions?.[mode]) {
                delete nextCache.pendingCustomInstructions[mode];
            }
            writeCache(nextCache);
            return saved;
        }
        catch (error) {
            const nextCache = readCache();
            nextCache.pendingCustomInstructions = {
                ...(nextCache.pendingCustomInstructions || {}),
                [mode]: normalized,
            };
            writeCache(nextCache);
            throw error;
        }
    }
    async getExcelColors() {
        const response = await requestJson({
            path: "/api/settings/excel-colors",
            method: "GET",
        });
        const normalized = normalizeExcelColors(response.excelColors);
        const cache = readCache();
        cache.excelColors = normalized;
        delete cache.pendingExcelColors;
        writeCache(cache);
        return normalized;
    }
    async putExcelColors(colors) {
        const normalized = normalizeExcelColors(colors);
        const cache = readCache();
        cache.excelColors = normalized;
        writeCache(cache);
        try {
            const response = await requestJson({
                path: "/api/settings/excel-colors",
                method: "PUT",
                payload: normalized,
            });
            const saved = normalizeExcelColors(response.excelColors || normalized);
            const nextCache = readCache();
            nextCache.excelColors = saved;
            delete nextCache.pendingExcelColors;
            writeCache(nextCache);
            return saved;
        }
        catch (error) {
            const nextCache = readCache();
            nextCache.pendingExcelColors = normalized;
            writeCache(nextCache);
            throw error;
        }
    }
    async flushPendingWrites() {
        if (this.flushPromise) {
            return this.flushPromise;
        }
        this.flushPromise = (async () => {
            const pending = readCache();
            const pendingCustom = pending.pendingCustomInstructions || {};
            const modes = ["powerpoint", "excel"];
            for (const mode of modes) {
                if (pendingCustom[mode]) {
                    try {
                        await this.putCustomInstructions(mode, pendingCustom[mode]);
                    }
                    catch {
                        // Keep pending entry for a future retry.
                    }
                }
            }
            if (pending.pendingExcelColors) {
                try {
                    await this.putExcelColors(pending.pendingExcelColors);
                }
                catch {
                    // Keep pending entry for a future retry.
                }
            }
        })();
        try {
            await this.flushPromise;
        }
        finally {
            this.flushPromise = null;
        }
    }
}
export const backendSettingsService = new BackendSettingsService();
